import { useEffect, useState } from "react";
import { Sparkles, Eye, EyeOff, CheckCircle2, XCircle, Loader2, KeyRound } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

import { tr, trServer } from "../lib/i18n";
/**
 * AI Assistant settings section. Admin-only edit; other roles see it read-only.
 * The plaintext API key is WRITE-ONLY over the API — the server returns only
 * a masked hint like `sk-ant-•••••4f2a`, plus `ai_key_set: bool`.
 */
export default function AiSettingsSection({ isAdmin, form, patch, refresh }) {
  const [keyInput, setKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok,kind,message,model}
  const [savingKey, setSavingKey] = useState(false);

  const enabled = !!form.ai_enabled;
  const provider = form.ai_provider || "anthropic";
  const model = form.ai_model || "";
  const keySet = !!form.ai_key_set;
  const unreadable = !!form.ai_key_unreadable;
  const hint = unreadable ? "" : form.ai_api_key_hint || "";
  const usage = form.ai_usage || null;

  useEffect(() => {
    setTestResult(null);
  }, [provider, model]);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const body = {};
      if (keyInput) body.api_key = keyInput;
      if (provider) body.provider = provider;
      if (model) body.model = model;
      const { data } = await api.post("/ai/test-connection", body);
      setTestResult(data);
      if (data.ok) toast.success(tr("x.connected_model_responded", { p0: data.model || model || "OK" }));
      else toast.error(trServer(data.message) || tr("x.test_failed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const saveKey = async () => {
    if (!keyInput || keyInput.length < 8) {
      toast.error(tr("x.paste_a_valid_api_key_first"));
      return;
    }
    setSavingKey(true);
    try {
      await api.post("/ai/config", {
        provider,
        model: model || null,
        api_key: keyInput,
      });
      setKeyInput("");
      setShowKey(false);
      toast.success(tr("x.api_key_saved_encrypted_at_rest"));
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm(tr("x.clear_the_ai_api_key_the_key_on_file_wil"))) return;
    setSavingKey(true);
    try {
      await api.post("/ai/config", { clear_key: true });
      toast.success(tr("x.api_key_cleared"));
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(false);
    }
  };

  const enableToggle = async (next) => {
    if (next && !keySet && !keyInput) {
      toast.error(tr("x.paste_an_api_key_first_then_enable_ai"));
      return;
    }
    try {
      await api.post("/ai/config", { ai_enabled: next });
      patch("ai_enabled", next);
      toast.success(next ? tr("x.ai_enabled") : tr("x.ai_disabled"));
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // -------- Read-only view for non-admins ----------
  if (!isAdmin) {
    return (
      <div className="panel p-5" data-testid="ai-settings-section-readonly">
        <div className="eyebrow mb-4 flex items-center gap-2">
          <Sparkles size={13} /> {tr("x.ai_assistant_read_only")}
        </div>
        <div className="text-sm">
          {enabled && keySet ? (
            <span>
              {tr("x.ai_is")} <strong className="text-emerald-500">{tr("x.enabled_2")}</strong> ({provider}
              {model ? ` · ${model}` : ""}{tr("x.contact_an_admin_to_change_the_configura")}
            </span>
          ) : (
            <span className="text-[var(--gray-400)]">
              {tr("x.ai_is")} <strong>{tr("x.disabled_2")}</strong> {tr("x.on_this_deployment_contact_an_admin_to_e")}
            </span>
          )}
          {usage?.month_calls != null && (
            <div className="mt-2 text-xs text-[var(--gray-400)]">
              {usage.month_calls} {tr("x.calls_this_month")}
              {usage.monthly_cap ? tr("x.cap", { p0: usage.monthly_cap }) : ""}
              {usage.last_success_at ? tr("x.last_success", { p0: new Date(usage.last_success_at).toLocaleString() }) : ""}
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------- Admin editor ----------
  return (
    <div className="panel p-5" data-testid="ai-settings-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2"><Sparkles size={13} /> {tr("x.ai_assistant")}</div>
          <p className="text-xs text-[var(--gray-400)] mt-1 max-w-2xl">
            {tr("x.enable_ai_assisted_description_rewriting")}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-xs uppercase tracking-wider text-[var(--gray-400)]">{tr("x.enabled")}</span>
          <input
            data-testid="ai-enabled-toggle"
            type="checkbox"
            checked={enabled}
            onChange={(e) => enableToggle(e.target.checked)}
            className="h-4 w-4"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className="eyebrow">{tr("x.provider")}</label>
          <select
            data-testid="ai-provider"
            className="input mt-2"
            value={provider}
            onChange={(e) => patch("ai_provider", e.target.value)}
          >
            <option value="anthropic">{tr("x.anthropic_claude")}</option>
            <option value="openai">{tr("x.openai_gpt")}</option>
          </select>
        </div>

        <div>
          <label className="eyebrow">{tr("x.model")}</label>
          <input
            data-testid="ai-model"
            className="input mt-2"
            value={model}
            placeholder={provider === "anthropic" ? "claude-haiku-4-5-20251001" : "gpt-4o-mini"}
            onChange={(e) => patch("ai_model", e.target.value)}
          />
          <p className="text-[11px] text-[var(--gray-400)] mt-1">
            {tr("x.a_smaller_faster_model_is_fine_descripti")}
          </p>
        </div>

        <div>
          <label className="eyebrow">{tr("x.monthly_cap_calls")}</label>
          <input
            data-testid="ai-monthly-cap"
            type="number"
            min="0"
            className="input mt-2"
            value={form.ai_monthly_cap ?? 500}
            onChange={(e) => patch("ai_monthly_cap", Number(e.target.value))}
          />
          <p className="text-[11px] text-[var(--gray-400)] mt-1">{tr("x.0_unlimited")}</p>
        </div>

        <div>
          <label className="eyebrow">{tr("x.per_user_hourly_limit")}</label>
          <input
            data-testid="ai-hourly-limit"
            type="number"
            min="1"
            className="input mt-2"
            value={form.ai_hourly_limit ?? 30}
            onChange={(e) => patch("ai_hourly_limit", Number(e.target.value))}
          />
        </div>

        <div className="md:col-span-2 lg:col-span-3">
          <label className="eyebrow flex items-center gap-1.5"><KeyRound size={12} /> {tr("x.api_key")}</label>
          <div className="mt-2 flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[280px]">
              <input
                data-testid="ai-api-key"
                className="input"
                style={{ paddingInlineEnd: 40 }}
                type={showKey ? "text" : "password"}
                autoComplete="off"
                spellCheck={false}
                placeholder={keySet && !unreadable ? hint || "••••••••" : tr("x.paste_key_here")}
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute end-2 top-1/2 -translate-y-1/2 text-[var(--gray-400)] hover:text-[var(--fg)]"
                aria-label={showKey ? tr("x.hide_key") : tr("x.show_key")}
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <button
              type="button"
              data-testid="ai-save-key"
              disabled={!keyInput || savingKey}
              onClick={saveKey}
              className="h-10 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold disabled:opacity-40"
            >
              {savingKey ? tr("x.saving") : tr("x.save_key")}
            </button>
            <button
              type="button"
              data-testid="ai-test-connection"
              disabled={testing || (!keyInput && !keySet)}
              onClick={testConnection}
              className="h-10 px-4 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {tr("x.test_ai_key")}
            </button>
            {keySet && (
              <button
                type="button"
                data-testid="ai-clear-key"
                onClick={clearKey}
                className="h-10 px-4 rounded-lg border border-red-500/40 text-red-500 text-sm"
              >
                {tr("x.clear_key")}
              </button>
            )}
          </div>
          {unreadable ? (
            <p className="text-sm font-semibold text-red-500 mt-2 flex items-center gap-1.5" data-testid="ai-key-unreadable">
              <XCircle size={14} /> {tr("x.saved_key_unreadable")}
            </p>
          ) : (
          <p className="text-[11px] text-[var(--gray-400)] mt-2" data-testid="ai-key-status">
            {keySet
              ? tr("x.a_key_is_on_file_paste_a_new_value_to_re", { p0: hint || "••••" })
              : tr("x.no_key_on_file_paste_your_anthropic_or_o")}
          </p>
          )}
          {testResult && (
            <div
              data-testid="ai-test-result"
              className={`mt-3 text-sm p-3 rounded-lg flex items-start gap-2 ${
                testResult.ok
                  ? "bg-emerald-500/10 text-emerald-500"
                  : "bg-red-500/10 text-red-500"
              }`}
            >
              {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
              <div>
                {testResult.ok ? (
                  <>{tr("x.connected_successfully_model")} <strong>{testResult.model}</strong> {tr("x.responded")}</>
                ) : (
                  <>{trServer(testResult.message)}</>
                )}
              </div>
            </div>
          )}
        </div>

        {usage && (
          <div className="md:col-span-2 lg:col-span-3">
            <div className="eyebrow mb-2">{tr("x.usage")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="panel p-3">
                <div className="text-xs text-[var(--gray-400)] mb-1">{tr("x.this_month")}</div>
                <div className="text-lg font-semibold">
                  {usage.month_calls} {usage.monthly_cap ? <span className="text-sm text-[var(--gray-400)]">/ {usage.monthly_cap}</span> : null}
                </div>
              </div>
              <div className="panel p-3">
                <div className="text-xs text-[var(--gray-400)] mb-1">{tr("x.remaining")}</div>
                <div className="text-lg font-semibold">
                  {usage.monthly_cap ? Math.max(0, usage.monthly_cap - usage.month_calls) : "∞"}
                </div>
              </div>
              <div className="panel p-3">
                <div className="text-xs text-[var(--gray-400)] mb-1">{tr("x.last_successful_call")}</div>
                <div className="text-sm">
                  {usage.last_success_at ? new Date(usage.last_success_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>
            {usage.on_cap && (
              <div className="mt-3 text-sm p-3 rounded-lg bg-amber-500/10 text-amber-500">
                {tr("x.monthly_quota_reached_ai_features_are_hi")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
